import { useLocale, useT } from "../../i18n";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatPriceShort } from "../../lib/format";
import { useState } from "react";
import { ChipGroup, MakeModelFields, MileageField, PriceFields, YearFields } from "./FilterControls";
import { AdvancedFilters } from "./AdvancedFilters";
import { SortSelect } from "./SortSelect";

const Panel = ({ id, label, summary, active, children, wide }) => (
  <Popover>
    <PopoverTrigger
      data-testid={`filter-trigger-${id}`}
      className={`group inline-flex h-11 items-center gap-2 border px-4 text-[12px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
        active ? "border-ink text-ink" : "border-ink/15 text-ink/75 hover:border-ink/50"
      }`}
    >
      <span>{label}</span>
      {summary && <span className="max-w-[9rem] truncate normal-case tracking-normal text-ink/55">· {summary}</span>}
      <span className="ml-1 h-1.5 w-1.5 rotate-45 border-b border-r border-current opacity-60 transition-transform duration-300 group-data-[state=open]:-rotate-[135deg]" aria-hidden />
    </PopoverTrigger>
    <PopoverContent align="start" sideOffset={8} data-testid={`filter-panel-${id}`} className={`${wide ? "w-[26rem]" : "w-80"} rounded-none border border-ink/15 bg-white p-6 text-ink shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)]`}>
      {children}
    </PopoverContent>
  </Popover>
);

const range = (a, b, f = (x) => x) => (a || b ? `${a ? f(a) : "…"}–${b ? f(b) : "…"}` : "");

export const FilterBar = ({ filters, update, setFilters, facets, total, onClear, activeCount }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const lang = useLocale();
  const props = { value: filters, onChange: update, facets, idPrefix: "desk" };
  const moreCount = ["fuel", "transmission", "drivetrain", "aspiration", "exterior_color", "equipment"].reduce((n, k) => n + (filters[k] ? filters[k].split(",").length : 0), 0)
    + ["market", "displacement_min", "displacement_max"].filter((k) => filters[k]).length;
  const bodyCount = filters.body_type ? filters.body_type.split(",").length : 0;
  return (
    <div className="hidden items-center justify-between gap-6 lg:flex" data-testid="filter-bar-desktop">
      <div className="flex flex-wrap items-center gap-2">
        <Panel id="make" label={t("filters.make")} summary={(() => { const m = facets?.makes?.find((x) => x.value === filters.make); return [m?.label || filters.make, m?.models.find((x) => x.value === filters.model)?.label || filters.model].filter(Boolean).join(" "); })()} active={!!filters.make}>
          <MakeModelFields {...props} />
        </Panel>
        <Panel id="year" label={t("filters.year")} summary={range(filters.year_from, filters.year_to)} active={!!(filters.year_from || filters.year_to)}>
          <YearFields {...props} />
        </Panel>
        <Panel id="price" label={t("filters.price")} summary={range(filters.price_min, filters.price_max, (p) => formatPriceShort(+p, lang))} active={!!(filters.price_min || filters.price_max)}>
          <PriceFields {...props} />
        </Panel>
        <Panel id="mileage" label={t("filters.mileage")} summary={filters.mileage_max ? `≤ ${(+filters.mileage_max).toLocaleString("en-US")} km` : ""} active={!!filters.mileage_max}>
          <MileageField {...props} />
        </Panel>
        <Panel id="body" label={t("filters.body")} summary={bodyCount ? String(bodyCount) : ""} active={!!bodyCount} wide>
          <ChipGroup label={t("filters.body")} field="body_type" enumKey="body_type" options={facets?.body_types} {...props} />
        </Panel>
        <button type="button" onClick={() => setOpen(true)} data-testid="filter-trigger-more"
          className={`inline-flex h-11 items-center gap-2 border px-4 text-[12px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${moreCount ? "border-ink text-ink" : "border-ink/15 text-ink/75 hover:border-ink/50"}`}>
          {t("filters.moreFilters")}{moreCount > 0 && <span className="normal-case tracking-normal text-ink/55">· {moreCount}</span>}
        </button>
        <AdvancedFilters open={open} onOpenChange={setOpen} filters={filters} setFilters={setFilters} facets={facets} />
        {activeCount > 0 && (
          <button type="button" onClick={onClear} data-testid="filter-clear-desktop" className="text-link ml-3 inline-flex min-h-[44px] items-center text-ink/60 hover:text-ink">
            <span className="text-link-label">{t("filters.clearAll")}</span>
          </button>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-6">
        <p className="micro-label tabular-nums text-ink/60" data-testid="inventory-count">
          {lang === "ja" ? `${total ?? "—"}${t("inventory.vehicles")}` : `${total ?? "—"} ${t("inventory.vehicles")}`}
        </p>
        <SortSelect value={filters.sort} scope={filters.scope} onChange={(s) => update({ sort: s })} id="desk-sort" />
      </div>
    </div>
  );
};
